/**
 * v-tooltip in the UI kit style (same look as <ui-tooltip-content>).
 *
 * Value: a string, or { content, title?, placement? }; placement can also be
 * given as a modifier (v-tooltip.left / .right / .bottom). Rendered as plain
 * text, appended to <body>.
 */

const PLACEMENTS = [ 'top', 'bottom', 'left', 'right' ];
const GAP = 8;
const VIEWPORT_MARGIN = 4;

const ARROW_CLASSES = {
  top: '-bottom-1 left-1/2 -translate-x-1/2',
  bottom: '-top-1 left-1/2 -translate-x-1/2',
  left: '-right-1 top-1/2 -translate-y-1/2',
  right: '-left-1 top-1/2 -translate-y-1/2',
};

function parseBinding(binding) {
  const value = binding.value;

  let content = '';
  let title = '';
  let placement = 'top';

  if (typeof value === 'string' || typeof value === 'number') {
    content = String(value);
  }
  else if (value && typeof value === 'object') {
    content = value.content ?? '';
    title = value.title ?? '';

    if (value.placement) {
      placement = value.placement;
    }
  }

  for (const candidate of PLACEMENTS) {
    if (binding.modifiers[candidate]) {
      placement = candidate;
    }
  }

  return { content: String(content), title: String(title), placement };
}

function hasText(options) {
  return options.content !== '' || options.title !== '';
}

function render(tooltip, options) {
  const arrow = document.createElement('span');
  arrow.className = 'absolute size-2.5 rotate-45 rounded-[2px] bg-foreground ' + ARROW_CLASSES[options.placement];

  const body = document.createElement('div');
  body.className = 'relative flex flex-col gap-0.5';

  if (options.title) {
    const title = document.createElement('div');
    title.className = 'text-[11px] leading-none opacity-70';
    title.textContent = options.title;
    body.appendChild(title);
  }

  const content = document.createElement('div');
  content.textContent = options.content;
  body.appendChild(content);

  tooltip.replaceChildren(arrow, body);
}

function buildTooltip(options) {
  const tooltip = document.createElement('div');
  tooltip.className = 'pointer-events-none fixed z-[10000] w-fit max-w-[220px] rounded-md bg-foreground px-3 py-1.5 text-xs text-balance break-words text-background opacity-0 shadow-md transition-opacity duration-150';
  tooltip.setAttribute('role', 'tooltip');
  render(tooltip, options);

  return tooltip;
}

function positionTooltip(tooltip, el, placement) {
  const rect = el.getBoundingClientRect();
  const tRect = tooltip.getBoundingClientRect();

  let top;
  let left;

  switch (placement) {
    case 'bottom':
      top = rect.bottom + GAP;
      left = rect.left + (rect.width - tRect.width) / 2;
      break;
    case 'left':
      top = rect.top + (rect.height - tRect.height) / 2;
      left = rect.left - tRect.width - GAP;
      break;
    case 'right':
      top = rect.top + (rect.height - tRect.height) / 2;
      left = rect.right + GAP;
      break;
    case 'top':
    default:
      top = rect.top - tRect.height - GAP;
      left = rect.left + (rect.width - tRect.width) / 2;
      break;
  }

  left = Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - tRect.width - VIEWPORT_MARGIN));
  top = Math.max(VIEWPORT_MARGIN, Math.min(top, window.innerHeight - tRect.height - VIEWPORT_MARGIN));

  tooltip.style.top = `${Math.round(top)}px`;
  tooltip.style.left = `${Math.round(left)}px`;
}

const STATE = new WeakMap();

function show(el) {
  const state = STATE.get(el);
  if (!state || state.tooltip || !hasText(state.options)) {
    return;
  }

  const tooltip = buildTooltip(state.options);
  document.body.appendChild(tooltip);
  state.tooltip = tooltip;

  positionTooltip(tooltip, el, state.options.placement);

  requestAnimationFrame(() => {
    if (state.tooltip === tooltip) {
      tooltip.classList.replace('opacity-0', 'opacity-100');
    }
  });
}

function hide(el) {
  const state = STATE.get(el);
  if (!state || !state.tooltip) {
    return;
  }

  const tooltip = state.tooltip;
  state.tooltip = null;
  tooltip.classList.replace('opacity-100', 'opacity-0');
  setTimeout(() => tooltip.remove(), 200);
}

/* eslint-disable-next-line local-rules/custom-naming-pattern */
const TooltipDirective = {
  mounted(el, binding) {
    const onEnter = () => show(el);
    const onLeave = () => hide(el);

    STATE.set(el, {
      options: parseBinding(binding),
      onEnter,
      onLeave,
      tooltip: null,
    });

    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('focus', onEnter);
    el.addEventListener('blur', onLeave);
  },

  updated(el, binding) {
    const state = STATE.get(el);
    if (!state) {
      return;
    }

    state.options = parseBinding(binding);

    if (state.tooltip) {
      if (!hasText(state.options)) {
        hide(el);

        return;
      }

      render(state.tooltip, state.options);
      positionTooltip(state.tooltip, el, state.options.placement);
    }
  },

  beforeUnmount(el) {
    const state = STATE.get(el);
    if (!state) {
      return;
    }

    el.removeEventListener('mouseenter', state.onEnter);
    el.removeEventListener('mouseleave', state.onLeave);
    el.removeEventListener('focus', state.onEnter);
    el.removeEventListener('blur', state.onLeave);

    if (state.tooltip) {
      state.tooltip.remove();
    }

    STATE.delete(el);
  },
};

export default TooltipDirective;
